import { EllipsisVerticalIcon } from "@heroicons/react/16/solid";
import { get } from "lodash-es";
import { Video } from "lucide-react";
import React, { FC, useCallback, useContext, useRef, useState } from "react";
import YouTube from "react-youtube";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  Chip,
  MenuItem,
  MenuList,
  Stack,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import { green, grey } from "@mui/material/colors";
import { useTheme } from "@mui/material/styles";
import {
  GridCellParams,
  GridColDef,
  GridFilterModel,
  GridListColDef,
  GridRenderCellParams,
  GridSortModel,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro";
import { GridRowSelectionModel } from "@mui/x-data-grid/models/gridRowSelectionModel";

import DataGridPro from "../../components/DataGridPro";
import DataGridProToolbar from "../../components/DataGridProToolbar";
import FeatureHint from "../../components/FeatureHint";
import Header from "../../components/Header";
import { LoadingError } from "../../components/LoadingError";
import Page from "../../components/Page";
import Quota from "../../components/Quota";
import Link from "../../components/styled/Link";
import SvgIcon from "../../components/styled/SvgIcon";
import useClusterColumn from "../../hooks/use-cluster-column";
import useIdColumn from "../../hooks/use-id-column";
import useNavigateWithSearch from "../../hooks/use-navigate-with-search";
import usePaginationModel from "../../hooks/use-pagination-model";
import { usePolicyRestrictedInHostedCluster } from "../../hooks/use-policy-restricted-in-hosted-cluster";
import { usePopover } from "../../hooks/use-popover";
import useQuota from "../../hooks/use-quota";
import { useSavedColumnWidths } from "../../hooks/use-saved-column-widths";
import { setBannerMessage } from "../../slices/pricing_info";
import { useDispatch, useSelector } from "../../store";
import {
  getFilter,
  getOrderBy,
  useListPoliciesQuery,
} from "../../store/enhancedApi";
import { Policy, useDeletePolicyMutation } from "../../store/pomeriumApi";
import { updatedAt } from "../../utils/data-grid-col-defs";
import PolicyActionToolbar from "./components/PolicyActionToolbar";
import PolicyDeleteActionDialog from "./components/PolicyDeleteActionDialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { UserContext } from "@/contexts/UserContext";
import sendStandardEvent from "@/hooks/use-firebase-event";

const defaultColumns = JSON.stringify({
  id: false,
  originatorId: false,
});

type AssignedChipProps = {
  label: string;
  color: Record<50 | 500 | 900, string>;
};
const AssignedChip: FC<AssignedChipProps> = ({ label, color }) => {
  return (
    <Chip
      size="small"
      label={label}
      sx={(theme) => ({
        textTransform: "uppercase",
        color: color[500],
        backgroundColor: color[theme.palette.mode === "dark" ? 900 : 50],
      })}
    />
  );
};

const POLICY_RESTRICTED_CREATE_MESSAGE =
  "To create policies in hosted clusters, upgrade your account.";
const POLICY_RESTRICTED_DELETE_MESSAGE =
  "To delete policies in hosted clusters, upgrade your account.";

const List: FC = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigateWithSearch();
  const dispatch = useDispatch();
  const clusterCol = useClusterColumn();
  const idCol = useIdColumn();
  const actionsPopover = usePopover();
  const anchorRef = useRef<HTMLElement | null>(null);
  const theme = useTheme();
  const isListView = useMediaQuery(theme.breakpoints.down("sm"));
  const [policyId, setPolicyId] = useState<string>("");
  const [canDelete, setCanDelete] = useState<boolean>(false);
  const [selected, setSelected] = useState<GridRowSelectionModel>([]);
  const [showDelete, setShowDelete] = useState(false);
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<string>(
    localStorage.getItem("policyColumns") || defaultColumns,
  );
  const { mutateColumnWidths, onColumnWidthChange } =
    useSavedColumnWidths("policies");
  const { byId: orgById } = useSelector((state) => state.organizations);
  const readOnly = get(orgById, organizationId + ".readOnly", false) as boolean;
  const isPolicyRestricted = usePolicyRestrictedInHostedCluster();
  const { count: policyCount, limit: policyLimit } = useQuota("policies");
  const [paginationModel, setPaginationModel] = usePaginationModel();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const {
    data: result,
    error,
    isLoading,
    isFetching,
  } = useListPoliciesQuery(
    {
      organizationId,
      namespaceId: clusterId,
      includeDescendants: true,
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      filter: getFilter(filterModel),
      orderBy: getOrderBy(sortModel),
    },
    { skip: !organizationId || !clusterId },
  );
  const policies = result?.rows || [];
  const selectedPolicies =
    selected
      ?.map((id) => policies.find((p) => p.id === id.toString()))
      ?.filter((p) => !!p) || [];
  const rowCount = result?.rowCount || 0;
  const estimatedRowCount = result?.estimatedRowCount || 0;

  const [deletePolicy] = useDeletePolicyMutation();

  const onAdd = (evt: React.MouseEvent): void => {
    evt.preventDefault();
    if (isPolicyRestricted) {
      dispatch(setBannerMessage(POLICY_RESTRICTED_CREATE_MESSAGE));
    } else if (readOnly) {
      dispatch(
        setBannerMessage("To add another policy, renew your subscription."),
      );
    } else if (policyCount >= policyLimit) {
      dispatch(
        setBannerMessage("To add another policy, upgrade your account."),
      );
    } else {
      navigate(`/app/management/policies/create`, { replace: true });
    }
  };

  async function handleMultiDelete() {
    if (isPolicyRestricted) {
      dispatch(setBannerMessage(POLICY_RESTRICTED_DELETE_MESSAGE));
      return;
    }
    await Promise.all(
      selected.map((id) =>
        deletePolicy({
          organizationId,
          policyId: id.toString(),
        }),
      ),
    );
  }

  const deleteOne = () => {
    if (isPolicyRestricted) {
      dispatch(setBannerMessage(POLICY_RESTRICTED_DELETE_MESSAGE));
      actionsPopover.handleClose();
      return;
    }
    setSelected([policyId]);
    setShowDelete(true);
    actionsPopover.handleClose();
  };

  const onCloseDeleteDialog = () => {
    setShowDelete(false);
    setSelected([]);
  };

  let columns: GridColDef<Policy>[] = [
    idCol,
    {
      display: "flex",
      headerName: "Originator",
      field: "originatorId",
      flex: 1,
    },
    {
      display: "flex",
      headerName: "Name",
      field: "name",
      flex: 2,
      renderCell: function NameCell(params: GridCellParams) {
        return (
          <Link to={`./${params.row?.id || ""}/edit`}>{params.row.name}</Link>
        );
      },
    },
    clusterCol,
    {
      display: "flex",
      headerName: "Assigned?",
      field: "assigned",
      flex: 1,
      valueGetter: (_value, row) => {
        return row?.routes?.length > 0;
      },
      type: "boolean",
      renderCell: function StatusCell(params) {
        return params.row?.enforced ? (
          <AssignedChip label="Enforced" color={green} />
        ) : params.row?.routes?.length > 0 ? (
          <AssignedChip label="Assigned" color={green} />
        ) : (
          <AssignedChip label="Unassigned" color={grey} />
        );
      },
      sortComparator: (v1, v2) => {
        const firstLength = v1?.length;
        const secondLength = v2?.length;
        return firstLength > secondLength ? -1 : 1;
      },
    },
    updatedAt,
    {
      display: "flex",
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: function action(params) {
        return (
          <Grid
            container
            spacing={0}
            direction="column"
            alignItems="center"
            justifyContent="center"
          >
            <IconButton
              onClick={(e) => {
                anchorRef.current = e.currentTarget;
                setPolicyId(params.row?.id || "");
                setCanDelete(
                  !params.row?.routes?.length && !isPolicyRestricted,
                );
                actionsPopover.handleOpen();
              }}
              sx={{ width: 4 }}
            >
              <SvgIcon color="disabled">
                <EllipsisVerticalIcon />
              </SvgIcon>
            </IconButton>
          </Grid>
        );
      },
    },
  ];
  columns = mutateColumnWidths(columns);

  function ListViewCell(params: GridRenderCellParams) {
    return (
      <Stack direction="row" justifyContent="space-between">
        <Link to={`./${params.row?.id || ""}/edit`}>{params.row.name}</Link>
        <IconButton
          onClick={(e) => {
            anchorRef.current = e.currentTarget;
            setPolicyId(params.row?.id || "");
            setCanDelete(!params.row?.routes?.length && !isPolicyRestricted);
            actionsPopover.handleOpen();
          }}
          sx={{ width: 4, p: 0, m: 0 }}
        >
          <SvgIcon color="disabled">
            <EllipsisVerticalIcon />
          </SvgIcon>
        </IconButton>
      </Stack>
    );
  }

  const listColDef: GridListColDef = {
    field: "listColumn",
    renderCell: ListViewCell,
  };

  const CustomToolbar: FC = useCallback(
    () => (
      <Stack direction="row" justifyContent="space-between">
        {!isListView && (
          <DataGridProToolbar>
            <PolicyActionToolbar
              onDelete={() => setShowDelete(true)}
              selectedPolicies={selectedPolicies}
            />
          </DataGridProToolbar>
        )}
        <GridToolbarQuickFilter variant="outlined" size="small" sx={{ m: 1 }} />
      </Stack>
    ),
    [JSON.stringify(selectedPolicies), setShowDelete],
  );

  if (error) {
    return (
      <Page title="Policies">
        <Header title="Policies" breadcrumbs={true} videoId="lRpkd20psbk" />
        <LoadingError />
      </Page>
    );
  }

  const actions = (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Quota quota="policies" label="policies" />
      <Button onClick={onAdd} variant="default">
        <AddCircleOutlineIcon aria-hidden="true" />
        <span className="sr-only md:not-sr-only">New Policy</span>
      </Button>
    </Stack>
  );

  return (
    <Page title="Policies">
      <Header title="Policies" breadcrumbs={true} actions={actions} />
      <FeatureHint
        hintId="policies"
        title="What are Policies?"
        learnMoreLink="https://pomerium.com/docs/capabilities/authorization"
      >
        <p>
          Pomerium enforces dynamic, context-aware authorization on every
          request. This capability extends across deployments of any size or
          complexity, from single-route use cases to multi-namespace or
          multi-cluster enterprise environments.
        </p>
        <p>
          <b>Policies</b> live on multiple levels:
        </p>
        <ul>
          <li>
            <b>Routes</b>: Policies can be attached to each route, controlling
            who and what can access the upstream service.
          </li>
          <li>
            <b>Clusters</b>: Zero-managed clusters pull their configuration
            (routes, policies, certificates) from a hosted control plane. Each
            cluster has its own environment. You can define policies in the Zero
            console, and they're synced to local Pomerium Core replicas.
          </li>
        </ul>
        <Accordion
          type="single"
          collapsible
          onValueChange={(value: string) => {
            if (value) {
              sendStandardEvent("video_hint_opened", "policies", user);
            }
          }}
        >
          <AccordionItem value="policies_video">
            <AccordionTrigger>
              <span className="flex gap-2 items-center">
                <Video color="#06AED4" />
                Want to watch a video about Policies?
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <YouTube
                videoId="3MJrNvQ7aIE"
                opts={{ height: "465", width: "825" }}
                title="Intro to Policies video"
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </FeatureHint>
      <PolicyDeleteActionDialog
        open={showDelete}
        entries={
          policies?.filter((r) => selected.includes(r?.id || "")) ||
          ([] as Policy[])
        }
        onClose={onCloseDeleteDialog}
        onDelete={handleMultiDelete}
      />
      <Popover
        anchorEl={anchorRef.current}
        disableScrollLock
        onClose={actionsPopover.handleClose}
        open={actionsPopover.open}
        slotProps={{
          paper: {
            sx: {
              minWidth: 260,
            },
          },
        }}
      >
        <MenuList>
          <MenuItem
            onClick={() =>
              navigate("/app/management/policies/" + policyId + "/edit")
            }
          >
            Edit
          </MenuItem>
          {canDelete && <MenuItem onClick={deleteOne}>Delete</MenuItem>}
          {!canDelete && (
            <Tooltip
              title={
                isPolicyRestricted
                  ? POLICY_RESTRICTED_DELETE_MESSAGE
                  : "This policy is currently assigned to one or more routes and so it cannot be deleted."
              }
            >
              <span>
                <MenuItem disabled>Delete</MenuItem>
              </span>
            </Tooltip>
          )}
        </MenuList>
      </Popover>
      <Container component={Paper} maxWidth={false} disableGutters={true}>
        <DataGridPro
          loading={isLoading || isFetching}
          rows={clusterId ? policies || [] : []}
          columns={columns}
          onRowSelectionModelChange={(model) => setSelected(model)}
          rowSelectionModel={selected}
          slots={{
            toolbar: CustomToolbar,
          }}
          columnVisibilityModel={JSON.parse(columnVisibilityModel)}
          onColumnVisibilityModelChange={(newModel) => {
            localStorage.setItem("policyColumns", JSON.stringify(newModel));
            setColumnVisibilityModel(JSON.stringify(newModel));
          }}
          onColumnWidthChange={onColumnWidthChange}
          unstable_listView={isListView}
          unstable_listColumn={listColDef}
          paginationMode="server"
          rowCount={rowCount}
          estimatedRowCount={estimatedRowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortingMode="server"
          onSortModelChange={setSortModel}
          onFilterModelChange={setFilterModel}
        />
      </Container>
    </Page>
  );
};
export default List;
